import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Check, X } from "lucide-react";
import { api } from "../../lib/api";
import { Metric } from "../Metric";
import { RewardSettingsPanel } from "./RewardSettingsPanel";
import { useSort, SortTh } from "../../lib/useSort";

const RD = [["phone", "User phone"], ["megatel_number", "Load to (Megatel)"], ["mb", "Amount"], ["requested_at", "Requested"], ["status", "Status"], ["processed_at", "Processed"], ["expires_at", "Expires"], ["terms_accepted_at", "T&Cs accepted"]];
const WL = [["phone", "Phone"], ["weeks_earned", "Weeks earned"], ["earned_mb", "Earned"], ["pending_mb", "Pending"], ["redeemed_mb", "Redeemed"], ["expired_mb", "Expired"], ["balance_mb", "Balance"], ["earned_this_week", "This week"], ["last_earned", "Last earned"]];
const acc = (cols) => Object.fromEntries(cols.map(([k]) => [k, (r) => (typeof r[k] === "boolean" ? Number(r[k]) : r[k])]));
const RD_ACC = acc(RD);
const WL_ACC = acc(WL);

const gb = (mb) => (mb >= 1000 ? `${(mb / 1000).toFixed(1)}GB` : `${mb}MB`);
const STATUS = { pending: "text-[#FF9F0A]", redeemed: "text-[#32D74B]", rejected: "text-neutral-500" };

export const RewardsAdmin = () => {
  const [reds, setReds] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [filter, setFilter] = useState("pending");

  const load = useCallback(() => Promise.all([api.redemptions(), api.wallets()]).then(([r, w]) => { setReds(r); setWallets(w); }), []);
  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  const mark = async (id, status) => {
    try { await api.markRedemption(id, status); toast.success(`Marked ${status}`); load(); } catch { toast.error("Update failed"); }
  };
  const exportXlsx = async () => {
    const blob = await api.exportRewards();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `cellcheckpoint-rewards-${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
  };

  const sum = (arr, k) => arr.reduce((s, x) => s + x[k], 0);
  const shown = useMemo(() => reds.filter((r) => filter === "all" || r.status === filter), [reds, filter]);
  const rd = useSort(shown, RD_ACC);
  const wl = useSort(wallets, WL_ACC);

  return (
    <div data-testid="rewards-admin">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="font-display text-3xl tracking-wide">Megatel Rewards</div>
        <button data-testid="export-rewards-button" onClick={exportXlsx} className="btn-primary px-4 py-2 text-xs flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4" /> Export Excel
        </button>
      </div>
      <RewardSettingsPanel />
      <div className="grid grid-cols-2 md:grid-cols-6 border-l border-b border-white/10">
        <Metric testId="rw-users" label="Users earning" value={wallets.length} />
        <Metric testId="rw-earned" label="Total earned" value={gb(sum(wallets, "earned_mb"))} />
        <Metric testId="rw-pending" label="Pending to load" value={gb(sum(wallets, "pending_mb"))} bad={sum(wallets, "pending_mb") > 0} />
        <Metric testId="rw-redeemed" label="Redeemed" value={gb(sum(wallets, "redeemed_mb"))} />
        <Metric testId="rw-expired" label="Expired (60d)" value={gb(sum(wallets, "expired_mb"))} />
        <Metric testId="rw-balance" label="Outstanding balance" value={gb(sum(wallets, "balance_mb"))} />
      </div>

      <div className="px-6 py-3 border-b border-white/10 flex items-center gap-2">
        <span className="label mr-2">Redemption requests</span>
        {["pending", "redeemed", "rejected", "all"].map((f) => (
          <button key={f} data-testid={`redemption-filter-${f}`} onClick={() => setFilter(f)} className={`btn-ghost text-xs px-2 py-1 ${filter === f ? "!text-white !border-[#007AFF]" : ""}`}>{f}</button>
        ))}
      </div>
      <table className="w-full text-xs" data-testid="redemptions-table">
        <thead className="text-neutral-500 text-left"><tr>{RD.map(([k, l]) => <SortTh key={k} k={k} label={l} sort={rd.sort} toggle={rd.toggle} className="px-6 py-2" />)}<th /></tr></thead>
        <tbody>
          {rd.sorted.map((r) => (
            <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`redemption-row-${r.id}`}>
              <td className="px-6 py-2 text-neutral-400">{r.phone}</td>
              <td className="px-6 py-2 text-white font-medium" data-testid={`redemption-megatel-${r.id}`}>{r.megatel_number || r.phone}</td>
              <td className="px-6 py-2">{gb(r.mb)}</td>
              <td className="px-6 py-2 text-neutral-500">{new Date(r.requested_at).toLocaleString()}</td>
              <td className={`px-6 py-2 uppercase ${STATUS[r.status]}`}>{r.status}</td>
              <td className="px-6 py-2 text-neutral-500">{r.processed_at ? new Date(r.processed_at).toLocaleString() : "—"}</td>
              <td className="px-6 py-2 text-neutral-400" data-testid={`redemption-expires-${r.id}`}>{r.expires_at ? `${new Date(r.expires_at).toLocaleDateString()} (${Math.max(0, Math.ceil((new Date(r.expires_at) - Date.now()) / 864e5))}d left)` : "—"}</td>
              <td className="px-6 py-2 text-neutral-400" data-testid={`redemption-terms-${r.id}`}>{r.terms_accepted_at ? <>{new Date(r.terms_accepted_at).toLocaleString()} <span className="text-neutral-600">v{r.terms_version}</span></> : <span className="text-neutral-600">Not recorded</span>}</td>
              <td className="px-6 py-2 text-right">
                {r.status === "pending" && (
                  <div className="flex justify-end gap-2">
                    <button data-testid={`mark-redeemed-${r.id}`} onClick={() => mark(r.id, "redeemed")} className="bg-[#32D74B] text-black px-3 py-1 flex items-center gap-1"><Check className="w-3 h-3" /> Mark redeemed</button>
                    <button data-testid={`mark-rejected-${r.id}`} onClick={() => mark(r.id, "rejected")} className="btn-ghost px-2 py-1"><X className="w-3 h-3" /></button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!shown.length && <div className="px-6 py-4 text-xs text-neutral-600">No {filter === "all" ? "" : filter} requests.</div>}

      <div className="px-6 py-3 border-y border-white/10 label mt-6">User wallets</div>
      <table className="w-full text-xs" data-testid="wallets-table">
        <thead className="text-neutral-500 text-left"><tr>{WL.map(([k, l]) => <SortTh key={k} k={k} label={l} sort={wl.sort} toggle={wl.toggle} className="px-6 py-2" />)}</tr></thead>
        <tbody>
          {wl.sorted.map((w) => (
            <tr key={w.phone} className="border-t border-white/5" data-testid={`wallet-row-${w.phone}`}>
              <td className="px-6 py-2 text-white">{w.phone}</td>
              <td className="px-6 py-2">{w.weeks_earned}</td>
              <td className="px-6 py-2">{gb(w.earned_mb)}</td>
              <td className="px-6 py-2 text-[#FF9F0A]">{w.pending_mb ? gb(w.pending_mb) : "—"}</td>
              <td className="px-6 py-2">{gb(w.redeemed_mb)}</td>
              <td className="px-6 py-2 text-neutral-500" data-testid={`wallet-expired-${w.phone}`}>{w.expired_mb ? gb(w.expired_mb) : "—"}</td>
              <td className="px-6 py-2 text-[#32D74B]">{gb(w.balance_mb)}</td>
              <td className="px-6 py-2">{w.earned_this_week ? "✓" : "—"}</td>
              <td className="px-6 py-2 text-neutral-500">{w.last_earned ? new Date(w.last_earned).toLocaleDateString() : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
